import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { PageHero } from "@/components/page-parts";
import {
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  PENINSULA_ADDRESS,
  PENINSULA_OFFICE,
} from "@/lib/firm";

/**
 * Internal playbook page for the firm — noindexed and unlinked from the public
 * navigation. It documents the Google Business Profile steps that sit outside
 * the codebase but drive local map-pack visibility.
 */
export const Route = createFileRoute("/google-business-profile")({
  head: () => ({
    meta: [
      { title: fitTitle(`Google Business Profile playbook (internal) | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Internal checklist for setting up, verifying and maintaining the Coastside Law Google Business Profile."),
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Coastside Law — Google Business Profile playbook" },
      { property: "og:description", content: "Internal checklist for Coastside Law's business profile setup, verification and maintenance." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: GbpPage,
});

const SETUP: ReadonlyArray<{ t: string; d: string }> = [
  {
    t: "1. Claim the listing",
    d: `Create or claim the profile at google.com/business using a firm email address (${FIRM.email}), not a personal one. Business name exactly "${FIRM.name}" — no keywords like "conveyancing Caloundra" appended, which is a suspension risk.`,
  },
  {
    t: "2. Address and service area",
    d: `Enter the address exactly as it appears sitewide: ${FIRM_ADDRESS}. It must match the website, letterhead and every directory character for character. Because the office is a home-based address, hide it from public view and set a service area instead (Golden Beach, Caloundra, Pelican Waters, Currimundi, Little Mountain, Wurtulla, Bribie Island) — Google requires this for businesses that serve clients at their location.`,
  },
  {
    t: "3. Verify",
    d: "Verification for law firms is usually video: a single unedited walkthrough showing the street or complex, signage or mail with the business name, the working space, and evidence you have authority (business documents, keys, bank card). Have QLS membership and firm registration paperwork on hand. Allow up to five business days.",
  },
  {
    t: "4. Categories",
    d: 'Primary category: "Conveyancer". Secondary categories: "Estate planning attorney", "Elder law attorney", "Law firm", "Real estate attorney". The primary category drives most of the map-pack ranking, so keep it on the highest-volume service.',
  },
  {
    t: "5. Profile content",
    d: `Phone ${FIRM.phone} (same number sitewide), website coastsidelaw.com.au, hours ${FIRM.hours.toLowerCase()}, opening date, and a description that mentions Golden Beach, Caloundra and fixed-fee conveyancing in plain English. Add services with the fixed fees, and link each service to the matching page on the site.`,
  },
  {
    t: "6. Photos",
    d: "Ten or more: exterior, interior, the two lawyer headshots, and the logo. Geotagging is not required, but real photos taken on a phone in the area perform better than stock. Add a few new photos every month — profiles with recent photo activity get more discovery views.",
  },
];

const REVIEWS: ReadonlyArray<{ t: string; d: string }> = [
  {
    t: "Ask at settlement",
    d: "The single best moment is the settlement confirmation email or call, while relief is fresh. Send the review link in that same message — one sentence, no template that reads like marketing.",
  },
  {
    t: "Use the short link",
    d: 'In the profile dashboard, use "Ask for reviews" to copy the g.page/r/... short link. Put it in the email signature and the settlement email. Never bulk-request, never gate ("only if you were happy"), and never offer anything in exchange — both Google and the QLS advertising rules are strict here.',
  },
  {
    t: "Reply to every review",
    d: "Reply within 48 hours. Keep replies short and never confirm or discuss anything about a client's matter, even a positive one — confidentiality survives the review. For a negative review, respond factually, invite them to call, and take the detail offline.",
  },
  {
    t: "Aim for pace, not volume",
    d: "Five to eight reviews in the first quarter, then two or three a month, beats twenty in one week — a sudden burst looks manufactured and can be filtered. Reviews that mention the service and the suburb (\"conveyancing on our Pelican Waters purchase\") carry the most local weight.",
  },
  {
    t: "Post monthly updates",
    d: "Use Google Posts for the fixed-fee offers, a new guide from the site, or a seasonal note (transfer duty changes, holiday settlement dates). Posts keep the profile active and appear in the branded search panel.",
  },
];

/**
 * Little Mountain is a service area of the Golden Beach profile, not a separate
 * listing — a profile at a suburb with no staffed premises is the most common
 * cause of suspension, and it would take the Golden Beach listing with it. The
 * suburb is worked through reviews that name it, service-area citations, and
 * the /conveyancing/little-mountain landing page.
 */
const LITTLE_MOUNTAIN_REVIEW = {
  link: "Use the g.page/r/... short link from “Ask for reviews” on the Golden Beach profile.",
  when: "In the settlement confirmation email, the same day settlement happens.",
  message: `Hi [name], settlement went through this morning and the keys are yours — congratulations.

If the process felt straightforward, would you mind leaving us a Google review? It genuinely helps other people in Little Mountain find a solicitor rather than guessing.

[review link]

No pressure either way, and thanks again for having us act for you.

[Erin / Michael]`,
  nudge:
    "If they mention Little Mountain, Bellvista or the school catchment in their own words, that suburb text is indexed on the profile — never ask them to include a keyword, but do mention where the property was in your own sentence above so the context is natural.",
  rules:
    "One request per client, never a follow-up chase, never gated on being happy, never anything offered in exchange. Both Google's policies and the QLS advertising rules apply.",
} as const;

const CITATIONS: ReadonlyArray<{ name: string; where: string; offices: string; url: string }> = [
  {
    name: "Queensland Law Society — Find a Solicitor",
    where: "QLS member portal",
    offices: "Golden Beach (principal) + Scarborough (additional place of practice)",
    url: "https://coastsidelaw.com.au/?utm_source=qls&utm_medium=referral&utm_campaign=directory",
  },
  {
    name: "Bing Places for Business",
    where: "bingplaces.com",
    offices: "One listing per office",
    url: "https://coastsidelaw.com.au/?utm_source=bing-places&utm_medium=referral&utm_campaign=directory-golden-beach",
  },
  {
    name: "Apple Business Connect",
    where: "businessconnect.apple.com",
    offices: "One listing per office",
    url: "https://coastsidelaw.com.au/?utm_source=apple-maps&utm_medium=referral&utm_campaign=directory-golden-beach",
  },
  {
    name: "Yellow Pages Australia",
    where: "yellowpages.com.au",
    offices: "Both offices",
    url: "https://coastsidelaw.com.au/?utm_source=yellowpages&utm_medium=referral&utm_campaign=directory",
  },
  {
    name: "True Local",
    where: "truelocal.com.au",
    offices: "Both offices",
    url: "https://coastsidelaw.com.au/?utm_source=truelocal&utm_medium=referral&utm_campaign=directory",
  },
  {
    name: "Hotfrog Australia",
    where: "hotfrog.com.au",
    offices: "Both offices",
    url: "https://coastsidelaw.com.au/?utm_source=hotfrog&utm_medium=referral&utm_campaign=directory",
  },
  {
    name: "Localsearch",
    where: "localsearch.com.au",
    offices: "Both offices",
    url: "https://coastsidelaw.com.au/?utm_source=localsearch&utm_medium=referral&utm_campaign=directory",
  },
  {
    name: "Yelp Australia",
    where: "biz.yelp.com",
    offices: "Both offices",
    url: "https://coastsidelaw.com.au/?utm_source=yelp&utm_medium=referral&utm_campaign=directory",
  },
  {
    name: "Caloundra Chamber of Commerce",
    where: "caloundrachamber.com.au",
    offices: "Golden Beach",
    url: "https://coastsidelaw.com.au/conveyancing/caloundra?utm_source=caloundra-chamber&utm_medium=referral&utm_campaign=directory",
  },
  {
    name: "Moreton Bay / Redcliffe Peninsula Chamber",
    where: "moretonbaychamber.com.au",
    offices: "Scarborough",
    url: "https://coastsidelaw.com.au/scarborough?utm_source=moretonbay-chamber&utm_medium=referral&utm_campaign=directory",
  },
];

/**
 * Service-area citations for Little Mountain. Each entry keeps the Golden Beach
 * NAP verbatim — Little Mountain is listed as an area served, never as an
 * address — and points at the suburb landing page so the lead is attributable.
 */
const LITTLE_MOUNTAIN_CITATIONS: ReadonlyArray<{
  name: string;
  where: string;
  offices: string;
  url: string;
}> = [
  {
    name: "Bing Places — service area update",
    where: "bingplaces.com",
    offices: "Golden Beach listing: add Little Mountain to areas served",
    url: "https://coastsidelaw.com.au/conveyancing/little-mountain?utm_source=bing-places&utm_medium=referral&utm_campaign=directory-little-mountain",
  },
  {
    name: "Apple Business Connect — service area update",
    where: "businessconnect.apple.com",
    offices: "Golden Beach listing: add Little Mountain to areas served",
    url: "https://coastsidelaw.com.au/conveyancing/little-mountain?utm_source=apple-maps&utm_medium=referral&utm_campaign=directory-little-mountain",
  },
  {
    name: "Yellow Pages Australia",
    where: "yellowpages.com.au",
    offices: "Golden Beach listing: Little Mountain in the service-area field",
    url: "https://coastsidelaw.com.au/conveyancing/little-mountain?utm_source=yellowpages&utm_medium=referral&utm_campaign=directory-little-mountain",
  },
  {
    name: "True Local",
    where: "truelocal.com.au",
    offices: "Golden Beach listing: Little Mountain suburb tag",
    url: "https://coastsidelaw.com.au/conveyancing/little-mountain?utm_source=truelocal&utm_medium=referral&utm_campaign=directory-little-mountain",
  },
  {
    name: "Hotfrog Australia",
    where: "hotfrog.com.au",
    offices: "Golden Beach listing: Little Mountain in areas served",
    url: "https://coastsidelaw.com.au/conveyancing/little-mountain?utm_source=hotfrog&utm_medium=referral&utm_campaign=directory-little-mountain",
  },
  {
    name: "Localsearch",
    where: "localsearch.com.au",
    offices: "Golden Beach listing: Little Mountain suburb tag",
    url: "https://coastsidelaw.com.au/conveyancing/little-mountain?utm_source=localsearch&utm_medium=referral&utm_campaign=directory-little-mountain",
  },
  {
    name: "Caloundra Chamber of Commerce",
    where: "caloundrachamber.com.au",
    offices: "Member profile — Little Mountain sits in the Caloundra catchment",
    url: "https://coastsidelaw.com.au/conveyancing/little-mountain?utm_source=caloundra-chamber&utm_medium=referral&utm_campaign=directory-little-mountain",
  },
  {
    name: "Sunshine Coast Council business directory",
    where: "sunshinecoast.qld.gov.au business listings",
    offices: "Golden Beach address, Little Mountain listed as an area served",
    url: "https://coastsidelaw.com.au/conveyancing/little-mountain?utm_source=scc-directory&utm_medium=referral&utm_campaign=directory-little-mountain",
  },
];
/**
 * Untagged Google Business Profile clicks land in analytics as "direct" because
 * Google strips the referrer on the profile website button. Tagging each link
 * with UTM parameters is the only way to attribute those enquiries.
 */
const UTM_LINKS: ReadonlyArray<{ where: string; url: string }> = [
  {
    where: "Golden Beach profile — website button",
    url: "https://coastsidelaw.com.au/?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach",
  },
  {
    where: "Golden Beach profile — appointment / booking link",
    url: "https://coastsidelaw.com.au/contact?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=booking",
  },
  {
    where: "Scarborough profile — website button",
    url: "https://coastsidelaw.com.au/scarborough?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough",
  },
  {
    where: "Scarborough profile — services: conveyancing",
    url: "https://coastsidelaw.com.au/conveyancing/scarborough?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=conveyancing",
  },
  {
    where: "Scarborough profile — services: wills & EPOA",
    url: "https://coastsidelaw.com.au/scarborough-wills-and-estates?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=wills",
  },
  {
    where: "Scarborough profile — services: body corporate",
    url: "https://coastsidelaw.com.au/body-corporate-scarborough?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=body-corporate",
  },
  {
    where: "Golden Beach profile — services: probate & deceased estates",
    url: "https://coastsidelaw.com.au/probate/golden-beach?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=probate",
  },
  {
    where: "Golden Beach profile — services: conveyancing",
    url: "https://coastsidelaw.com.au/conveyancing/caloundra?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=conveyancing",
  },
  {
    where: "Golden Beach profile — website button (local pack landing page)",
    url: "https://coastsidelaw.com.au/conveyancing-near-me?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=near-me",
  },
  {
    where: "Both profiles — Google Posts targeting “conveyancing near me”",
    url: "https://coastsidelaw.com.au/conveyancing-near-me?utm_source=google&utm_medium=organic&utm_campaign=gbp-post&utm_content=near-me",
  },
  {
    where: "Scarborough profile — services: probate & deceased estates",
    url: "https://coastsidelaw.com.au/probate/scarborough?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=probate",
  },
  {
    where: "Golden Beach profile — services: conveyancing (Pelican Waters)",
    url: "https://coastsidelaw.com.au/conveyancing/pelican-waters?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=conveyancing-pelican-waters",
  },
  {
    where: "Both profiles — Google Posts targeting \u201cconveyancing Pelican Waters\u201d",
    url: "https://coastsidelaw.com.au/conveyancing/pelican-waters?utm_source=google&utm_medium=organic&utm_campaign=gbp-post&utm_content=pelican-waters",
  },
  {
    where: "Golden Beach profile — services: conveyancing (Little Mountain)",
    url: "https://coastsidelaw.com.au/conveyancing/little-mountain?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=conveyancing-little-mountain",
  },
  {
    where: "Golden Beach profile — monthly Google Post for Little Mountain",
    url: "https://coastsidelaw.com.au/conveyancing/little-mountain?utm_source=google&utm_medium=organic&utm_campaign=gbp-post&utm_content=little-mountain",
  },
  {
    where: "Golden Beach profile — services: selling a property",
    url: "https://coastsidelaw.com.au/selling/golden-beach?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=selling",
  },
  {
    where: "Golden Beach profile — services: selling (Caloundra)",
    url: "https://coastsidelaw.com.au/selling/caloundra?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=selling-caloundra",
  },
  {
    where: "Scarborough profile — services: selling a property",
    url: "https://coastsidelaw.com.au/selling/scarborough?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=selling",
  },
  {
    where: "Both profiles — selling hub (Google Posts about seller disclosure)",
    url: "https://coastsidelaw.com.au/selling?utm_source=google&utm_medium=organic&utm_campaign=gbp-selling&utm_content=hub",
  },
  {
    where: "Both profiles — probate hub (Google Posts about executors)",
    url: "https://coastsidelaw.com.au/probate?utm_source=google&utm_medium=organic&utm_campaign=gbp-probate&utm_content=hub",
  },
  {
    where:
      "Scarborough profile — service item \u201cProbate & letters of administration (Brisbane & QLD)\u201d",
    url: "https://coastsidelaw.com.au/probate-solicitor-brisbane?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=probate-brisbane",
  },
  {
    where: "Both profiles — Google Posts answering \u201chow long does probate take in QLD\u201d",
    url: "https://coastsidelaw.com.au/probate-solicitor-brisbane?utm_source=google&utm_medium=organic&utm_campaign=gbp-probate&utm_content=brisbane-longtail",
  },
  {
    where:
      "Both profiles — \u201cFrom the business\u201d / description link and the wills & probate overview post",
    url: "https://coastsidelaw.com.au/wills-and-probate-queensland?utm_source=google&utm_medium=organic&utm_campaign=gbp-entity&utm_content=wills-probate-overview",
  },
  {
    where: "Golden Beach profile — services: wills & EPOA (Golden Beach)",
    url: "https://coastsidelaw.com.au/wills/golden-beach?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=wills-golden-beach",
  },

  {
    where: "Golden Beach profile — services: wills & EPOA (Caloundra)",
    url: "https://coastsidelaw.com.au/wills/caloundra?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=wills-caloundra",
  },
  {
    where: "Golden Beach profile — services: wills & EPOA (Pelican Waters)",
    url: "https://coastsidelaw.com.au/wills/pelican-waters?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=wills-pelican-waters",
  },
  {
    where: "Golden Beach profile — services: probate (Caloundra)",
    url: "https://coastsidelaw.com.au/probate/caloundra?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=probate-caloundra",
  },
  {
    where: "Golden Beach profile — services: probate (Pelican Waters)",
    url: "https://coastsidelaw.com.au/probate/pelican-waters?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=probate-pelican-waters",
  },
  {
    where: "Scarborough profile — services: wills & EPOA (Redcliffe)",
    url: "https://coastsidelaw.com.au/wills/redcliffe?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=wills-redcliffe",
  },
  {
    where: "Scarborough profile — services: wills & EPOA (North Lakes)",
    url: "https://coastsidelaw.com.au/wills/north-lakes?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=wills-north-lakes",
  },
  {
    where: "Scarborough profile — services: probate (North Lakes)",
    url: "https://coastsidelaw.com.au/probate/north-lakes?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=probate-north-lakes",
  },
  {
    where: "Google Posts and photo captions",
    url: "https://coastsidelaw.com.au/<page>?utm_source=google&utm_medium=organic&utm_campaign=gbp-post",
  },
];


/**
 * The exact value to paste into each profile's fields. Every profile field that
 * can hold a URL points at a tagged page, so no map-pack click lands untagged.
 */
const PROFILE_TARGETS: ReadonlyArray<{
  profile: string;
  rows: ReadonlyArray<{ field: string; url: string }>;
}> = [
  {
    profile: "Golden Beach profile (Erin — 0488 340 853)",
    rows: [
      {
        field: "Website",
        url: "https://coastsidelaw.com.au/?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=website-button",
      },
      {
        field: "Appointment link",
        url: "https://coastsidelaw.com.au/contact?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=booking",
      },
      {
        field: "Service — conveyancing",
        url: "https://coastsidelaw.com.au/conveyancing/caloundra?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=conveyancing",
      },
      {
        field: "Service — selling a property",
        url: "https://coastsidelaw.com.au/selling/golden-beach?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=selling",
      },
      {
        field: "Service — wills & EPOA",
        url: "https://coastsidelaw.com.au/wills-and-estates?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=wills",
      },
      {
        field: "Service — probate & executors",
        url: "https://coastsidelaw.com.au/executor-queensland?utm_source=google&utm_medium=organic&utm_campaign=gbp-golden-beach&utm_content=probate",
      },
    ],
  },
  {
    profile: "Scarborough profile (Michael — 0419 000 884)",
    rows: [
      {
        field: "Website",
        url: "https://coastsidelaw.com.au/scarborough?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=website-button",
      },
      {
        field: "Appointment link",
        url: "https://coastsidelaw.com.au/contact?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=booking",
      },
      {
        field: "Service — conveyancing",
        url: "https://coastsidelaw.com.au/conveyancing/scarborough?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=conveyancing",
      },
      {
        field: "Service — selling a property",
        url: "https://coastsidelaw.com.au/selling/scarborough?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=selling",
      },
      {
        field: "Service — wills & EPOA",
        url: "https://coastsidelaw.com.au/scarborough-wills-and-estates?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=wills",
      },
      {
        field: "Service — body corporate",
        url: "https://coastsidelaw.com.au/body-corporate-scarborough?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=body-corporate",
      },
      {
        field: "Service — probate & executors",
        url: "https://coastsidelaw.com.au/probate/scarborough?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=probate",
      },
      {
        field: "Service — probate & letters of administration (Brisbane & QLD)",
        url: "https://coastsidelaw.com.au/probate-solicitor-brisbane?utm_source=google&utm_medium=organic&utm_campaign=gbp-scarborough&utm_content=probate-brisbane",
      },
    ],
  },
];

const SECOND_PROFILE: ReadonlyArray<{ t: string; d: string }> = [
  {
    t: "A second profile is allowed — but only as a real office",
    d: "Google permits one profile per staffed location. 15 Fortune Street, Scarborough qualifies only if it is genuinely attended by appointment and signed. Same rules as the first: exact name \"Coastside Law\", no keywords in the name, address character-for-character identical to the site.",
  },
  {
    t: "Give it its own phone and landing page",
    d: "Use Michael's direct line 0419 000 884 on the Scarborough profile and point the website button at /scarborough, not the homepage. A profile that points at the homepage competes with the Golden Beach listing for the same page and neither ranks well locally.",
  },
  {
    t: "Categories for the peninsula listing",
    d: 'Primary "Conveyancer"; secondary "Estate planning attorney", "Elder law attorney", "Law firm". Add services that mirror the pages: fixed-fee sale and purchase, simple will, will + EPOA pack, couples pack, probate, body corporate advice — each linked to its page with the UTM link below.',
  },
  {
    t: "Reviews stay separate",
    d: "Reviews from peninsula clients must be left on the Scarborough profile, not the Golden Beach one. Send Michael's clients the Scarborough short link from that profile's dashboard.",
  },
];

const BRISBANE: ReadonlyArray<{ t: string; d: string }> = [
  {
    t: "Do not create a separate Brisbane profile",
    d: "A profile requires a staffed address in that city where clients can be met and mail received. We have Golden Beach and Scarborough, so a Brisbane listing would be a guideline breach — and Google does not suspend the offending listing alone, it can take down every profile on the account and the reviews with them. A virtual office, a co-working desk or a mail drop does not cure it.",
  },
  {
    t: "Scarborough is the Brisbane-facing profile",
    d: "Scarborough is inside the Brisbane metropolitan area for Google's purposes, so it is the listing that can legitimately reach Brisbane searchers. Set its service area to Redcliffe, Scarborough, Margate, Clontarf, Rothwell, North Lakes, Deception Bay, Caboolture, Brisbane Northside, Brisbane CBD and Brisbane Southside. Service areas do not rank like a physical address — they widen who sees the profile in discovery searches, they do not put us in the Brisbane CBD map pack.",
  },
  {
    t: "Probate is the service that travels",
    d: "Conveyancing and wills searches in Brisbane are proximity-bound and we will not win them from Scarborough. Probate is different: it is a single state-wide Supreme Court registry, the work is done by email and video, and executors search state-wide terms. That is why the Brisbane-facing service item on the Scarborough profile points at /probate-solicitor-brisbane rather than the homepage.",
  },
  {
    t: "Where the probate page goes on the profile",
    d: 'Scarborough profile → Services → add "Probate & letters of administration (Brisbane & QLD)" with the tagged /probate-solicitor-brisbane URL below. Keep the profile\'s main Website button on /scarborough — that is the page that supports the physical listing. Use the probate page as the destination for Google Posts aimed at executors instead.',
  },
  {
    t: "Rank Brisbane organically, not in the map pack",
    d: "Brisbane probate enquiries come from the blue links and AI answers, not the three-pack. /probate-solicitor-brisbane, /brisbane, /executor-queensland and /probate-queensland are the pages doing that work. Judge Brisbane performance on those pages in Search Console, not on map-pack position.",
  },
];

/** How the two live profiles divide the work day to day. */
const SIDE_BY_SIDE: ReadonlyArray<{
  row: string;
  gb: string;
  sc: string;
}> = [
  {
    row: "Owner",
    gb: "Erin Wilkinson",
    sc: "Michael Klein",
  },
  {
    row: "Phone on the profile",
    gb: "0488 340 853",
    sc: "0419 000 884",
  },
  {
    row: "Website button",
    gb: "/ (homepage, tagged gbp-golden-beach)",
    sc: "/scarborough (tagged gbp-scarborough)",
  },
  {
    row: "Service area",
    gb: "Golden Beach, Caloundra, Pelican Waters, Currimundi, Little Mountain, Wurtulla, Bribie Island, Baringa",
    sc: "Redcliffe peninsula, Moreton Bay, Brisbane Northside, Brisbane CBD, Brisbane Southside",
  },
  {
    row: "Lead service",
    gb: "Fixed-fee conveyancing",
    sc: "Wills & EPOA, body corporate, probate",
  },
  {
    row: "Brisbane-facing service item",
    gb: "None",
    sc: "Probate & letters of administration → /probate-solicitor-brisbane",
  },
  {
    row: "Google Posts",
    gb: "Weekly — settlement tips, seller disclosure, local market notes",
    sc: "Weekly — wills pricing, unit/body corporate issues, executor duties",
  },
  {
    row: "Reviews",
    gb: "Coast clients only, asked at settlement",
    sc: "Peninsula and Brisbane clients only, asked at grant or settlement",
  },
  {
    row: "Photos",
    gb: "Golden Beach office, Erin, coast streetscapes",
    sc: "Fortune Street office, Michael, peninsula streetscapes",
  },
];

const SIDE_BY_SIDE_RULES: ReadonlyArray<string> = [
  "Never post the same photo, post or description text on both profiles — duplicate content across listings suppresses one of them.",
  "Never point both profiles at the same landing page; they then compete for the same URL and neither ranks.",
  "Keep each profile's phone number distinct. Two listings sharing one number reads as a duplicate to Google.",
  "Reviews cannot be moved between profiles. If a Brisbane client leaves one on Golden Beach it is lost to the wrong listing — send the right short link at the right moment.",
  "Check both dashboards monthly and compare against the profile-click breakdown on the internal analytics dashboard, so the map-pack numbers and the on-site numbers are reconciled.",
];




function GbpPage() {
  return (
    <>
      <PageHero
        eyebrow="Internal — not indexed"
        title="Google Business Profile playbook"
        lead="The map pack is where most conveyancing enquiries start. This page is for the firm, not for clients — it is noindexed and not linked in the navigation."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">Setup and verification</h2>
        <div className="mt-8 space-y-6">
          {SETUP.map((s) => (
            <div key={s.t} className="border-l-2 border-accent pl-5">
              <h3 className="font-serif text-xl text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Reviews</h2>
        <div className="mt-8 space-y-6">
          {REVIEWS.map((s) => (
            <div key={s.t} className="rounded-sm border border-border bg-card p-5">
              <h3 className="font-serif text-xl text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Second profile — Scarborough
        </h2>
        <div className="mt-8 space-y-6">
          {SECOND_PROFILE.map((s) => (
            <div key={s.t} className="border-l-2 border-accent pl-5">
              <h3 className="font-serif text-xl text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Brisbane — how we cover it without a Brisbane profile
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          We have no staffed Brisbane address, so there is no lawful Brisbane listing to build.
          Brisbane is covered by the Scarborough profile's service area plus the probate pages on
          the site, with the probate page carrying the Brisbane-facing service link.
        </p>
        <div className="mt-8 space-y-6">
          {BRISBANE.map((s) => (
            <div key={s.t} className="border-l-2 border-accent pl-5">
              <h3 className="font-serif text-xl text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Running the two profiles side by side
        </h2>
        <div className="mt-8 overflow-x-auto rounded-sm border border-border">
          <table className="w-full min-w-[38rem] border-collapse text-left text-sm">
            <thead>
              <tr className="bg-secondary/60">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  &nbsp;
                </th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Golden Beach
                </th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Scarborough (Brisbane-facing)
                </th>
              </tr>
            </thead>
            <tbody>
              {SIDE_BY_SIDE.map((r) => (
                <tr key={r.row} className="border-t border-border align-top">
                  <th className="px-4 py-3 text-left font-semibold text-foreground">{r.row}</th>
                  <td className="px-4 py-3 leading-relaxed text-muted-foreground">{r.gb}</td>
                  <td className="px-4 py-3 leading-relaxed text-muted-foreground">{r.sc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="mt-6 space-y-3">
          {SIDE_BY_SIDE_RULES.map((r) => (
            <li
              key={r}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm leading-relaxed text-muted-foreground"
            >
              {r}
            </li>
          ))}
        </ul>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Stop profile clicks showing as "direct"
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Google strips the referrer from the profile's website button, so those visits arrive with
          no source and get counted as direct traffic. Tag every outbound link on both profiles with
          the UTM parameters below and the enquiry form will record where the person came from.
          Paste each URL in full — the tracking is in the query string, and the site records it
          against every enquiry.
        </p>
        <h3 className="mt-10 font-serif text-xl text-foreground">
          Paste these into the profile fields
        </h3>
        <div className="mt-5 space-y-6">
          {PROFILE_TARGETS.map((p) => (
            <div key={p.profile} className="rounded-sm border border-border bg-card p-5">
              <p className="text-sm font-semibold text-foreground">{p.profile}</p>
              <ul className="mt-3 space-y-3">
                {p.rows.map((r) => (
                  <li key={r.field} className="border-l-2 border-accent pl-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      {r.field}
                    </p>
                    <code className="mt-1 block break-all font-mono text-xs text-muted-foreground">
                      {r.url}
                    </code>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="mt-6 rounded-sm border border-primary/40 bg-primary/5 p-4 text-sm leading-relaxed text-muted-foreground">
          Every one of those clicks is now counted. The site records the campaign and service tag
          on arrival, and the{" "}
          <a href="/analytics" className="font-semibold text-primary underline">
            internal analytics dashboard
          </a>{" "}
          shows profile clicks broken down by page, by profile and by service link — plus which of
          them turned into an enquiry. Counts only; nothing identifying a visitor is stored.
        </p>

        <h3 className="mt-10 font-serif text-xl text-foreground">Other tagged links</h3>
        <ul className="mt-5 space-y-3">
          {UTM_LINKS.map((l) => (
            <li key={l.where} className="rounded-sm border border-border bg-card p-4">
              <p className="text-sm font-semibold text-foreground">{l.where}</p>
              <code className="mt-2 block break-all font-mono text-xs text-muted-foreground">
                {l.url}
              </code>
            </li>
          ))}
        </ul>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Little Mountain</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Little Mountain is worked as a service area of the Golden Beach profile. There is no
          separate listing and there should never be one — a profile at a suburb with no staffed
          premises is the most common cause of suspension, and it would take the Golden Beach
          listing down with it. The suburb is won instead through the landing page, service-area
          citations and reviews that name it.
        </p>

        <h3 className="mt-8 font-serif text-xl text-foreground">Service-area directory listings</h3>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Update the existing Golden Beach entry in each directory below — same name, same address,
          same phone, with Little Mountain added to the areas served. Use the tagged link so the
          enquiry is attributable in{" "}
          <a href="/analytics" className="font-semibold text-primary underline">
            /analytics
          </a>
          .
        </p>
        <ul className="mt-4 grid gap-3">
          {LITTLE_MOUNTAIN_CITATIONS.map((c) => (
            <li
              key={c.name}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              <p className="font-semibold text-foreground">{c.name}</p>
              <p className="mt-1">
                {c.where} — {c.offices}
              </p>
              <p className="mt-2 break-all font-mono text-xs">{c.url}</p>
            </li>
          ))}
        </ul>

        <h3 className="mt-10 font-serif text-xl text-foreground">Review request</h3>
        <div className="mt-4 rounded-sm border border-border bg-card p-5 text-sm text-muted-foreground">
          <p>
            <span className="font-semibold text-foreground">When:</span>{" "}
            {LITTLE_MOUNTAIN_REVIEW.when}
          </p>
          <p className="mt-2">
            <span className="font-semibold text-foreground">Link:</span>{" "}
            {LITTLE_MOUNTAIN_REVIEW.link}
          </p>
          <pre className="mt-4 whitespace-pre-wrap rounded-sm bg-secondary/60 p-4 font-sans text-sm leading-relaxed text-foreground">
            {LITTLE_MOUNTAIN_REVIEW.message}
          </pre>
          <p className="mt-4">{LITTLE_MOUNTAIN_REVIEW.nudge}</p>
          <p className="mt-2">{LITTLE_MOUNTAIN_REVIEW.rules}</p>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Keep the details identical everywhere</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Name, address and phone must be character-for-character the same on the profile, this
          site and every directory below. The site already emits LocalBusiness structured data on
          the contact page with this exact address and phone, so mismatches elsewhere are what
          weaken the signal.
        </p>
        <div className="mt-6 rounded-sm border border-border bg-secondary/50 p-5 text-sm text-muted-foreground">
          <p className="font-semibold text-foreground">{FIRM.name}</p>
          <p className="mt-1">{FIRM_ADDRESS}</p>
          <p>{FIRM.phone}</p>
        </div>
        <div className="mt-6 rounded-sm border border-border bg-secondary/50 p-5 text-sm text-muted-foreground">
          <p className="font-semibold text-foreground">
            {FIRM.name} — {PENINSULA_OFFICE.label}
          </p>
          <p className="mt-1">{PENINSULA_ADDRESS}</p>
          <p>
            {MICHAEL_PHONE} — {PENINSULA_OFFICE.note}
          </p>
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Enter the tagged link below in each directory&rsquo;s website field. Every one lands on
          the site, and the enquiry forms record the source before emailing {FIRM.email}, so
          /analytics can show which directory produced the lead.
        </p>
        <ul className="mt-4 grid gap-3">
          {CITATIONS.map((c) => (
            <li
              key={c.name}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              <p className="font-semibold text-foreground">{c.name}</p>
              <p className="mt-1">
                {c.where} — {c.offices}
              </p>
              <p className="mt-2 break-all font-mono text-xs">{c.url}</p>
            </li>
          ))}
        </ul>
      </section>
      <div className="h-20" />
    </>
  );
}
